import { instance } from '@rsos/sinatra';
import { getAPIHost } from '@rsos/utils/metaTags';
import type { PreconfiguredResponse, ApiResponse } from './types';

const baseURL = `${getAPIHost()}/v1/tf911/psap`;

/**
 * Fetch all preconfigured messages for an organization
 *
 * @param org - Organization identifier
 * @returns Promise with the API response containing preconfigured messages
 */
export const fetchMessagesApi = async (org: string) => {
  const response = await instance.get<ApiResponse>(
    `${baseURL}/${org}/preconfigured-responses`,
    { headers: { retry: true } },
  );
  return response;
};

/**
 * Create a new preconfigured message
 *
 * @param org - Organization identifier
 * @param message - Message data to create
 * @returns Promise with the created message
 */
export const createMessageApi = async (
  org: string,
  message: Partial<PreconfiguredResponse>,
) => {
  const response = await instance.post<PreconfiguredResponse>(
    `${baseURL}/${org}/preconfigured-responses`,
    message,
    { headers: { retry: true } },
  );
  return response;
};

/**
 * Update an existing preconfigured message
 *
 * @param org - Organization identifier
 * @param id - ID of the message to update
 * @param updates - Partial message data to update
 * @returns Promise with the updated message
 */
export const updateMessageApi = async (
  org: string,
  id: string,
  updates: Partial<PreconfiguredResponse>,
) => {
  const response = await instance.patch<PreconfiguredResponse>(
    `${baseURL}/${org}/preconfigured-responses/${id}`,
    updates,
    { headers: { retry: true } },
  );
  return response;
};

/**
 * Delete a preconfigured message
 *
 * @param org - Organization identifier
 * @param id - ID of the message to delete
 * @returns Promise that resolves when deletion is complete
 */
export const deleteMessageApi = async (org: string, id: string) => {
  const response = await instance.delete(
    `${baseURL}/${org}/preconfigured-responses/${id}`,
    { headers: { retry: true } },
  );
  return response;
};
